// Time-range control: relative-vs-absolute mode toggle + presets + zoom.
//
// In relative mode the user picks 1h/6h/24h/3d (anchored to NOW). In
// absolute mode two `<input type="datetime-local">` boxes show the live
// `{start,end}` and accept edits. The mode toggle never throws away the
// current view: switching to absolute freezes the live window; switching
// back to relative re-anchors to NOW with the current zoomHours preset.

import { fromLocalInput, toLocalInput } from "../helpers/window";
import type { TimelineWindowApi } from "../hooks/useTimelineWindow";

const PRESETS = [
  { z: 1, label: "1h" },
  { z: 6, label: "6h" },
  { z: 24, label: "24h" },
  { z: 72, label: "3d" },
];

export interface RangeControlProps {
  window: TimelineWindowApi;
}

export function RangeControl({ window: w }: RangeControlProps): JSX.Element {
  const onAbsStart = (v: string): void => {
    const ms = fromLocalInput(v);
    if (ms !== null) w.setAbsolute(ms, w.win.end);
  };
  const onAbsEnd = (v: string): void => {
    const ms = fromLocalInput(v);
    if (ms !== null) w.setAbsolute(w.win.start, ms);
  };
  return (
    <div className="rec-rangectl">
      <div className="rec-seg-group" role="group" aria-label="Time range mode">
        <button
          type="button"
          className={w.mode === "relative" ? "on" : ""}
          onClick={() => w.flipMode("relative")}
        >
          Relative
        </button>
        <button
          type="button"
          className={w.mode === "absolute" ? "on" : ""}
          onClick={() => w.flipMode("absolute")}
        >
          Absolute
        </button>
      </div>
      {w.mode === "relative" && (
        <div className="rec-seg-group" role="group" aria-label="Relative window">
          {PRESETS.map((p) => (
            <button
              type="button"
              key={p.z}
              className={w.presetMatch === p.z ? "on" : ""}
              onClick={() => w.setRelative(p.z)}
              aria-pressed={w.presetMatch === p.z}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
      {w.mode === "absolute" && (
        <div className="rec-absinputs">
          <input
            type="datetime-local"
            step={1}
            className="rec-dtinput"
            value={toLocalInput(w.win.start)}
            onChange={(e) => onAbsStart(e.target.value)}
            aria-label="Window start"
          />
          <span className="rec-arrow" aria-hidden="true">
            →
          </span>
          <input
            type="datetime-local"
            step={1}
            className="rec-dtinput"
            value={toLocalInput(w.win.end)}
            onChange={(e) => onAbsEnd(e.target.value)}
            aria-label="Window end"
          />
        </div>
      )}
      <div className="rec-seg-group" role="group" aria-label="Zoom timeline">
        <button
          type="button"
          className="rec-icon-btn"
          onClick={() => w.zoom(1.8, 0.5)}
          aria-label="Zoom out"
          title="Zoom out"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.2-3.2M8 11h6" />
          </svg>
        </button>
        <button
          type="button"
          className="rec-icon-btn"
          onClick={() => w.zoom(1 / 1.8, 0.5)}
          aria-label="Zoom in"
          title="Zoom in"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.2-3.2M11 8v6M8 11h6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
